import { installCanvasErrorDiagnostics } from './canvas-error-diagnostics.js';

export type RuntimeErrorSourceEvent =
  | { type: 'window_error'; event: ErrorEvent }
  | { type: 'unhandled_rejection'; event: PromiseRejectionEvent }
  | { type: 'console_error'; args: unknown[] }
  | { type: 'console_warn'; args: unknown[] };

type Subscriber = (event: RuntimeErrorSourceEvent) => void;

const subscribers = new Set<Subscriber>();
let installed = false;
let uninstallCanvasDiagnostics: (() => void) | null = null;
let previousConsoleError: typeof console.error | null = null;
let previousConsoleWarn: typeof console.warn | null = null;
let wrappedConsoleError: typeof console.error | null = null;
let wrappedConsoleWarn: typeof console.warn | null = null;

function publish(event: RuntimeErrorSourceEvent): void {
  for (const subscriber of [...subscribers]) {
    try {
      subscriber(event);
    } catch (error) {
      writeRuntimeSourceConsoleError('Runtime error subscriber failed:', error);
    }
  }
}

function onWindowError(event: ErrorEvent): void {
  publish({ type: 'window_error', event });
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  publish({ type: 'unhandled_rejection', event });
}

function install(): void {
  if (installed) return;
  installed = true;
  uninstallCanvasDiagnostics = installCanvasErrorDiagnostics();
  previousConsoleError = console.error;
  previousConsoleWarn = console.warn;
  const writeError = previousConsoleError.bind(console);
  const writeWarning = previousConsoleWarn.bind(console);
  wrappedConsoleError = (...args: unknown[]) => {
    writeError(...args);
    publish({ type: 'console_error', args });
  };
  wrappedConsoleWarn = (...args: unknown[]) => {
    writeWarning(...args);
    publish({ type: 'console_warn', args });
  };
  console.error = wrappedConsoleError;
  console.warn = wrappedConsoleWarn;
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
}

function uninstall(): void {
  if (!installed) return;
  installed = false;
  uninstallCanvasDiagnostics?.();
  uninstallCanvasDiagnostics = null;
  window.removeEventListener('error', onWindowError);
  window.removeEventListener('unhandledrejection', onUnhandledRejection);
  if (previousConsoleError && console.error === wrappedConsoleError) console.error = previousConsoleError;
  if (previousConsoleWarn && console.warn === wrappedConsoleWarn) console.warn = previousConsoleWarn;
  previousConsoleError = null;
  previousConsoleWarn = null;
  wrappedConsoleError = null;
  wrappedConsoleWarn = null;
}

/** Writes diagnostics without feeding them back into runtime-error analytics. */
export function writeRuntimeSourceConsoleError(...args: unknown[]): void {
  (previousConsoleError ?? console.error).apply(console, args);
}

export function subscribeRuntimeErrorSource(subscriber: Subscriber): () => void {
  install();
  subscribers.add(subscriber);
  let subscribed = true;
  return () => {
    if (!subscribed) return;
    subscribed = false;
    subscribers.delete(subscriber);
    if (subscribers.size === 0) uninstall();
  };
}
